/**
 * Object Node Renderer
 *
 * Renders object-type nodes with key-value pairs.
 * Each field is rendered intelligently based on its detected type:
 * - Markdown text → rendered markdown
 * - Image URLs / data URIs → inline images
 * - JSON objects/arrays → formatted code blocks
 * - URLs → clickable links
 * - Booleans → styled badges
 * - Numbers → styled inline
 * - Plain text / long text → appropriate text display
 *
 * Always renders at full detail (no LOD).
 */

import type { UINode, ScreenMetrics } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult } from '../renderer';
import type { MeasureOptions } from '../content-measure';
import { classifyField, renderFieldValue, type ClassifiedField } from './object-fields';

/** Keys to exclude from rendering */
const EXCLUDED_KEYS = new Set(['x', 'y', 'width', 'height', 'type', 'id', '_ui', 'rotation', 'canvas_type', 'renderAs']);

/**
 * Object renderer for generic object nodes.
 */
export class ObjectRenderer implements NodeRenderer {
	private lastContent: Map<string, string> = new Map();

	render(node: UINode, _metrics: ScreenMetrics, layers: RenderLayers): RenderResult {
		const isNewElement = layers.html.isNewlyCreated(node.id);
		const content = this.renderContent(node);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
		}

		return { lod: 'full', changed: contentChanged };
	}

	private renderContent(node: UINode): string {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const typeLabel = this.getTypeLabel(obj);
		const id = (node.data.id as string) ?? node.id;
		const color = this.getTypeColor(typeLabel);
		const fields = this.getClassifiedFields(obj);

		// Build field HTML with type-aware rendering
		const fieldsHtml = fields.length > 0
			? fields.map((field) => {
				const valueHtml = renderFieldValue(field);
				return `
				<div class="object-field" data-field-type="${field.type}">
					<div class="object-field-key">${this.escapeHtml(field.key)}</div>
					<div class="object-field-value">${valueHtml}</div>
				</div>
			`;
			}).join('')
			: '<div class="object-no-content">No fields</div>';

		return `
			<div class="object-node object-full">
				<div class="node-close-btn" data-action="close">&times;</div>
				<div class="object-header">
					<div class="object-title">
						<span class="object-type" style="background: ${color}; border-color: ${color}">${this.escapeHtml(typeLabel)}</span>
					</div>
					<div class="object-id">${this.escapeHtml(id)}</div>
				</div>
				<div class="object-fields">
					${fieldsHtml}
				</div>
			</div>
		`;
	}

	private getTypeLabel(obj: Record<string, unknown>): string {
		return typeof obj?.type === 'string' ? (obj.type as string) : 'object';
	}

	private getClassifiedFields(obj: Record<string, unknown>): ClassifiedField[] {
		const fields: ClassifiedField[] = [];

		for (const [key, value] of Object.entries(obj)) {
			if (EXCLUDED_KEYS.has(key)) continue;
			fields.push(classifyField(key, value));
		}

		return fields;
	}

	private getTypeColor(type: string): string {
		// Generate consistent color from type
		let hash = 0;
		for (let i = 0; i < type.length; i++) {
			hash = type.charCodeAt(i) + ((hash << 5) - hash);
		}
		const hue = Math.abs(hash % 360);
		// Use purple as base for most types (matching the original design)
		if (type === 'object') return 'rgba(124, 58, 237, 0.85)';
		// Lightness 35% ensures white text meets WCAG 3:1 contrast on all hues
		return `hsl(${hue}, 65%, 35%)`;
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	getContentHTML(node: UINode): string {
		return this.renderContent(node);
	}

	getMeasureOptions(_node: UINode): MeasureOptions {
		return { maxWidth: 520, minWidth: 200, minHeight: 80 };
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 360,
			height: node.size?.height ?? 160
		};
	}

	cleanup(nodeId: string): void {
		this.lastContent.delete(nodeId);
	}
}
